import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Ticket, Wallet, Gift, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/lang";

const Kpi = ({ label, value, sub, icon: Icon, gold, testid }) => (
  <div className={`cp-card ${gold ? "cp-card--gold" : ""}`} data-testid={testid}>
    <div className="cp-card__head">
      <span className="cp-card__label">{label}</span>
      <Icon size={16} style={{ color: gold ? "#f59e0b" : "#94a3b8" }} />
    </div>
    <div className="cp-card__value">{value}</div>
    {sub && <div className="cp-card__sub">{sub}</div>}
  </div>
);

export default function ClientDashboard() {
  const { lang } = useLang();
  const [me, setMe] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/client/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/client/bookings").then((r) => setRecent((r.data || []).slice(0, 4))).catch(() => {});
  }, []);

  const s = me?.stats || {};
  const locale = lang.toLowerCase() === "pt" ? "pt-PT" : lang.toLowerCase();
  const first = me?.name?.split(" ")[0] || "";

  return (
    <div data-testid="client-dashboard">
      <div className="cp-page-head">
        <span className="cp-eyebrow">Painel</span>
        <h1 className="cp-title">
          Olá, <span className="accent">{first || "cliente"}</span>
        </h1>
        <p className="cp-desc">
          Reservas, cashback e códigos de convite num só lugar. Tudo transparente e sempre à mão.
        </p>
      </div>

      <div className="cp-grid cp-grid--kpis" style={{ marginBottom: 40 }}>
        <Kpi
          testid="kpi-bookings"
          label="Reservas"
          value={s.total_bookings ?? "—"}
          sub={`${s.completed_bookings || 0} concluídas`}
          icon={Ticket}
        />
        <Kpi
          testid="kpi-spent"
          label="Total gasto"
          value={`€${(s.total_spent || 0).toFixed(2)}`}
          sub="Sem taxas surpresa"
          icon={Wallet}
        />
        <Kpi
          testid="kpi-cashback"
          label="Cashback"
          value={`€${(s.cashback_balance || 0).toFixed(2)}`}
          sub="Aplicado automaticamente"
          icon={Sparkles}
          gold
        />
        <Kpi
          testid="kpi-referrals"
          label="Convidados"
          value={s.referral_count ?? "—"}
          sub="5% de cashback por amigo"
          icon={Gift}
        />
      </div>

      <div className="cp-grid cp-grid--2">
        <div className="cp-card">
          <div className="cp-section-head">
            <div>
              <span className="cp-card__label">Histórico</span>
              <div className="cp-section-head__title" style={{ marginTop: 4 }}>Reservas recentes</div>
            </div>
            <Link to="/client-portal/bookings" className="cp-section-head__link" data-testid="see-all-bookings">
              Ver todas <ArrowRight size={12} />
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="cp-empty" data-testid="dashboard-empty-bookings">
              Ainda não tem reservas. Explore os nossos tours ou reserve um transfer.
            </div>
          ) : (
            <div>
              {recent.map((b) => (
                <div key={b.id} className="cp-booking-item" data-testid={`recent-booking-${b.id}`}>
                  <div className="cp-booking-item__ref">
                    {b.reference} · <span style={{ color: "#94a3b8", fontWeight: 400 }}>{b.type}</span>
                  </div>
                  <div className="cp-booking-item__meta">
                    {new Date(b.service_datetime).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                  <div className="cp-booking-item__meta" style={{ marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {b.pickup_address} → {b.destination_address}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cp-ref-hero" data-testid="dashboard-referral-hero">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Gift size={18} style={{ color: "#f59e0b" }} />
            <span className="cp-eyebrow" style={{ marginBottom: 0 }}>O seu código</span>
          </div>
          <div className="cp-ref-code" data-testid="referral-code">{me?.referral_code || "…"}</div>
          <p style={{ color: "#94a3b8", fontSize: "0.9rem", lineHeight: 1.55 }}>
            Partilhe o seu código e ganhe 5% de cashback em cada reserva paga pelos seus amigos. Sem limite.
          </p>
          <Link to="/client-portal/referrals" className="cp-btn-outline" style={{ marginTop: 20 }} data-testid="see-referrals">
            Ver convidados <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}
